// Similar to ConversationFilters.tsx
// Handles:
// - Event type filtering (message_sent, message_received, note_created, etc.)
// - Source filtering (twilio, email, manual, system)
// - Date range filtering
// - Status filtering
// - Associated record type filtering
// - Advanced filters popup with search functionality 

import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Typography,
  alpha,
  Popover,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Autocomplete,
  useTheme,
  Skeleton,
  Badge,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Fade,
  Zoom,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Message as MessageIcon,
  Note as NoteIcon,
  Forum as ForumIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Code as CodeIcon,
  Event as EventIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  FilterAlt as FilterAltIcon,
  AutoAwesome as AutoAwesomeIcon,
  Tune as TuneIcon,
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'src/store';
import {
  selectEventFilters,
  setFilters,
  clearFilters,
  fetchEvents,
  fetchTimelineEvents,
  selectEventsLoading,
  createEvent,
  selectAllEvents,
} from 'src/features/timeline/events.slice';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Styled components
const StyledChip = motion(Chip);
const MotionBox = motion(Box);

// Enhanced Event type options with colors
const EVENT_TYPE_OPTIONS = [
  { value: 'message_sent', label: 'Message Sent', icon: <MessageIcon />, color: '#4CAF50' },
  { value: 'message_received', label: 'Message Received', icon: <MessageIcon />, color: '#2196F3' },
  { value: 'message_delivered', label: 'Message Delivered', icon: <MessageIcon />, color: '#00BCD4' },
  { value: 'note_created', label: 'Note Created', icon: <NoteIcon />, color: '#FF9800' },
  { value: 'note_updated', label: 'Note Updated', icon: <NoteIcon />, color: '#FFC107' },
  { value: 'conversation_created', label: 'Conversation Created', icon: <ForumIcon />, color: '#9C27B0' },
  { value: 'conversation_status_changed', label: 'Status Changed', icon: <ForumIcon />, color: '#673AB7' },
  { value: 'contact_created', label: 'Contact Created', icon: <PersonIcon />, color: '#E91E63' },
  { value: 'contact_updated', label: 'Contact Updated', icon: <PersonIcon />, color: '#F44336' },
  { value: 'phone_call', label: 'Phone Call', icon: <PhoneIcon />, color: '#3F51B5' },
  { value: 'email_sent', label: 'Email Sent', icon: <EmailIcon />, color: '#009688' },
  { value: 'email_received', label: 'Email Received', icon: <EmailIcon />, color: '#00ACC1' },
  { value: 'system', label: 'System Event', icon: <CodeIcon />, color: '#607D8B' },
];

// Enhanced Source options with badges
const SOURCE_OPTIONS = [
  { value: 'twilio', label: 'Twilio', badge: 'SMS', color: '#F22F46' },
  { value: 'email', label: 'Email', badge: 'SMTP', color: '#EA4335' },
  { value: 'manual', label: 'Manual', badge: 'USER', color: '#34A853' },
  { value: 'system', label: 'System', badge: 'AUTO', color: '#4285F4' },
  { value: 'api', label: 'API', badge: 'REST', color: '#FBBC04' },
];

// Status options - updated to match actual data
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', indicator: '🟢' },
  { value: 'completed', label: 'Completed', indicator: '✅' },
  { value: 'pending', label: 'Pending', indicator: '🟡' },
  { value: 'inactive', label: 'Inactive', indicator: '⚪' },
];

// Quick filter presets - updated
const QUICK_FILTERS = [
  { label: 'Today', icon: <EventIcon />, filter: () => ({ created_after: new Date().toISOString().split('T')[0] }) },
  { label: 'Notes', icon: <NoteIcon />, filter: () => ({ event_type: 'notesUpdated' }) },
  { label: 'Messages', icon: <MessageIcon />, filter: () => ({ event_type: 'textMessagesCreated' }) },
  { label: 'Active', icon: <CheckCircleIcon />, filter: () => ({ status: 'active' }) },
];

const TimelineFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const filters = useAppSelector(selectEventFilters);
  const loading = useAppSelector(selectEventsLoading);
  const events = useAppSelector(selectAllEvents);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createEventData, setCreateEventData] = useState({
    event_type: '',
    source: 'manual',
    message: '',
    description: '',
    status: 'active',
  });
  
  // Local filter state
  const [localFilters, setLocalFilters] = useState(filters);

  const handleOpenFilters = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setLocalFilters(filters);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  const handleApplyFilters = () => {
    dispatch(setFilters(localFilters));
    dispatch(fetchEvents({
      ...localFilters,
      created_after: localFilters.created_after ? new Date(localFilters.created_after) : undefined,
      created_before: localFilters.created_before ? new Date(localFilters.created_before) : undefined,
      full_payload: false,
    }));
    handleCloseFilters();
    enqueueSnackbar('Filters applied', { variant: 'success' });
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchEvents({ full_payload: false }));
    setLocalFilters({});
    handleCloseFilters();
    enqueueSnackbar('Filters cleared', { variant: 'info' });
  };

  const handleLocalFilterChange = (field: string, value: any) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(fetchEvents({ limit: 50, full_payload: false })).unwrap();
      enqueueSnackbar('Timeline refreshed', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to refresh', { variant: 'error' });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleQuickFilter = (filterFn: () => any) => {
    const quickFilter = filterFn();
    dispatch(setFilters(quickFilter));
    dispatch(fetchEvents({ ...quickFilter, full_payload: false }));
    enqueueSnackbar('Quick filter applied', { variant: 'success' });
  };

  const handleCreateEvent = async () => {
    if (!createEventData.event_type || !createEventData.message) {
      enqueueSnackbar('Please fill in required fields', { variant: 'warning' });
      return;
    }

    try {
      await dispatch(createEvent(createEventData)).unwrap();
      enqueueSnackbar('Event created successfully', { variant: 'success' });
      setShowCreateDialog(false);
      setCreateEventData({
        event_type: '',
        source: 'manual',
        message: '',
        description: '',
        status: 'active',
      });
      // Refresh the timeline
      dispatch(fetchEvents({ limit: 50, full_payload: false }));
    } catch (error) {
      enqueueSnackbar('Failed to create event', { variant: 'error' });
    }
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;
  const isPopoverOpen = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {/* Title with animation */}
      <MotionBox
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <AutoAwesomeIcon sx={{ color: theme.palette.primary.main }} />
          Timeline
        </Typography>
        {events.length > 0 && (
          <Chip
            label={`${events.length} total`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.75rem',
              bgcolor: alpha(theme.palette.text.primary, 0.1),
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          />
        )}
      </MotionBox>

      {/* Loading skeleton for filters */}
      {loading && activeFilterCount === 0 ? (
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={100} height={32} />
          <Skeleton variant="rounded" width={80} height={32} />
          <Skeleton variant="rounded" width={120} height={32} />
        </Stack>
      ) : (
        <AnimatePresence>
          {/* Active filter chips with animations */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {filters.event_type && (
              <StyledChip
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                label={`Type: ${EVENT_TYPE_OPTIONS.find(o => o.value === filters.event_type)?.label || filters.event_type}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => {
                  const updatedFilters = { ...filters, event_type: undefined };
                  dispatch(setFilters(updatedFilters));
                  dispatch(fetchEvents({
                    ...updatedFilters,
                    created_after: updatedFilters.created_after ? new Date(updatedFilters.created_after) : undefined,
                    created_before: updatedFilters.created_before ? new Date(updatedFilters.created_before) : undefined,
                    full_payload: false,
                  }));
                }}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: 'white',
                    },
                  },
                }}
              />
            )}
            
            {filters.source && (
              <StyledChip
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                label={`Source: ${SOURCE_OPTIONS.find(o => o.value === filters.source)?.label || filters.source}`}
                size="small"
                color="secondary"
                variant="filled"
                onDelete={() => {
                  const updatedFilters = { ...filters, source: undefined };
                  dispatch(setFilters(updatedFilters));
                  dispatch(fetchEvents({
                    ...updatedFilters,
                    created_after: updatedFilters.created_after ? new Date(updatedFilters.created_after) : undefined,
                    created_before: updatedFilters.created_before ? new Date(updatedFilters.created_before) : undefined,
                    full_payload: false,
                  }));
                }}
              />
            )}

            {filters.status && (
              <StyledChip
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                label={`Status: ${filters.status}`}
                size="small"
                color="warning"
                variant="filled"
                onDelete={() => {
                  const updatedFilters = { ...filters, status: undefined };
                  dispatch(setFilters(updatedFilters));
                  dispatch(fetchEvents({
                    ...updatedFilters,
                    created_after: updatedFilters.created_after ? new Date(updatedFilters.created_after) : undefined,
                    created_before: updatedFilters.created_before ? new Date(updatedFilters.created_before) : undefined,
                    full_payload: false,
                  }));
                }}
              />
            )}

            {filters.search && (
              <StyledChip
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                label={`Search: ${filters.search}`}
                size="small"
                color="info"
                variant="filled"
                icon={<SearchIcon />}
                onDelete={() => {
                  const updatedFilters = { ...filters, search: undefined };
                  dispatch(setFilters(updatedFilters));
                  dispatch(fetchEvents({
                    ...updatedFilters,
                    created_after: updatedFilters.created_after ? new Date(updatedFilters.created_after) : undefined,
                    created_before: updatedFilters.created_before ? new Date(updatedFilters.created_before) : undefined,
                    full_payload: false,
                  }));
                }}
              />
            )}
          </Stack>
        </AnimatePresence>
      )}

      {/* Quick filter buttons */}
      <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
        {QUICK_FILTERS.map((qf, index) => (
          <Tooltip key={index} title={`Quick filter: ${qf.label}`}>
            <IconButton
              size="small"
              onClick={() => handleQuickFilter(qf.filter)}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              {qf.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Stack>

      {/* Action buttons */}
      <Stack direction="row" spacing={1}>
        {/* Create Event Button */}
        <Tooltip title="Create new event">
          <IconButton
            onClick={() => setShowCreateDialog(true)}
            sx={{
              bgcolor: theme.palette.success.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.success.dark,
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>

        {/* Refresh button with animation */}
        <Tooltip title="Refresh timeline">
          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        {/* Filter button with badge */}
        <Tooltip title="Advanced filters">
          <Badge badgeContent={activeFilterCount} color="primary">
            <IconButton
              onClick={handleOpenFilters}
              sx={{
                bgcolor: anchorEl ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.1),
                color: anchorEl ? 'white' : theme.palette.primary.main,
                '&:hover': {
                  bgcolor: anchorEl ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <TuneIcon />
            </IconButton>
          </Badge>
        </Tooltip>
      </Stack>

      {/* Enhanced Filters popover */}
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        TransitionComponent={Zoom}
        PaperProps={{
          elevation: 20,
          sx: { 
            width: 450, 
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
            p: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <FilterAltIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Advanced Filters
          </Typography>
        </Box>

        <Box sx={{ p: 3, pt: 5 }}>
          <Stack spacing={3}>
            {/* Event Type with enhanced UI */}
            <Autocomplete
              value={localFilters.event_type || null}
              onChange={(_, value) => handleLocalFilterChange('event_type', value)}
              options={EVENT_TYPE_OPTIONS.map(o => o.value)}
              getOptionLabel={(option) => EVENT_TYPE_OPTIONS.find(o => o.value === option)?.label || option}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Event Type" 
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const item = EVENT_TYPE_OPTIONS.find(o => o.value === option);
                return (
                  <Box 
                    component="li" 
                    {...props} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      '&:hover': {
                        bgcolor: alpha(item?.color || theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Box sx={{ color: item?.color }}>
                      {item?.icon}
                    </Box>
                    <Typography variant="body2">{item?.label}</Typography>
                  </Box>
                );
              }}
            />

            {/* Source with badges */}
            <FormControl size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={localFilters.source || ''}
                onChange={(e) => handleLocalFilterChange('source', e.target.value)}
                label="Source"
                startAdornment={
                  <InputAdornment position="start">
                    <CodeIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="">All Sources</MenuItem>
                {SOURCE_OPTIONS.map(option => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{option.label}</span>
                    <Chip 
                      label={option.badge} 
                      size="small" 
                      sx={{ 
                        ml: 2,
                        bgcolor: option.color,
                        color: 'white',
                        fontSize: '0.65rem',
                        height: 20,
                      }} 
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status with indicators */}
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.status || ''}
                onChange={(e) => handleLocalFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{option.indicator}</span>
                      <span>{option.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Enhanced Search */}
            <TextField
              label="Search"
              size="small"
              value={localFilters.search || ''}
              onChange={(e) => handleLocalFilterChange('search', e.target.value)}
              placeholder="Search in messages, descriptions, and payloads..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            <Divider sx={{ my: 1 }} />

            {/* Date Range with icon */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRangeIcon fontSize="small" />
                Date Range
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack spacing={2}>
                  <DatePicker
                    label="From Date"
                    value={localFilters.created_after ? new Date(localFilters.created_after) : null}
                    onChange={(date) => handleLocalFilterChange('created_after', date?.toISOString())}
                    slotProps={{ 
                      textField: { 
                        size: 'small', 
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        },
                      } 
                    }}
                  />
                  <DatePicker
                    label="To Date"
                    value={localFilters.created_before ? new Date(localFilters.created_before) : null}
                    onChange={(date) => handleLocalFilterChange('created_before', date?.toISOString())}
                    slotProps={{ 
                      textField: { 
                        size: 'small', 
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        },
                      } 
                    }}
                  />
                </Stack>
              </LocalizationProvider>
            </Box>

            {/* Action buttons with animations */}
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={loading ? <CircularProgress size={16} /> : <ClearIcon />}
                disabled={loading}
                fullWidth
                sx={{
                  borderColor: loading ? theme.palette.grey[300] : theme.palette.grey[400],
                  color: loading ? theme.palette.grey[500] : theme.palette.grey[700],
                  '&:hover': {
                    borderColor: theme.palette.grey[600],
                    bgcolor: alpha(theme.palette.grey[400], 0.1),
                  },
                  '&.Mui-disabled': {
                    borderColor: theme.palette.grey[300],
                  },
                }}
              >
                {loading ? 'Processing...' : 'Clear All'}
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilterListIcon />}
                disabled={loading}
                fullWidth
                sx={{
                  background: loading 
                    ? alpha(theme.palette.grey[500], 0.8) 
                    : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  boxShadow: loading ? 'none' : '0 3px 5px 2px rgba(33, 203, 243, .3)',
                  '&:hover': {
                    background: loading 
                      ? alpha(theme.palette.grey[500], 0.8)
                      : `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                  },
                  '&.Mui-disabled': {
                    color: 'white',
                  },
                }}
              >
                {loading ? 'Applying...' : 'Apply Filters'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>

      {/* Create Event Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddIcon />
            <Typography variant="h6">Create New Event</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Event Type *</InputLabel>
              <Select
                value={createEventData.event_type}
                onChange={(e) => setCreateEventData({ ...createEventData, event_type: e.target.value })}
                label="Event Type *"
              >
                {EVENT_TYPE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Message *"
              value={createEventData.message}
              onChange={(e) => setCreateEventData({ ...createEventData, message: e.target.value })}
              fullWidth
            />
            
            <TextField
              label="Description"
              value={createEventData.description}
              onChange={(e) => setCreateEventData({ ...createEventData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            
            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                value={createEventData.source}
                onChange={(e) => setCreateEventData({ ...createEventData, source: e.target.value })}
                label="Source"
              >
                {SOURCE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={createEventData.status}
                onChange={(e) => setCreateEventData({ ...createEventData, status: e.target.value })}
                label="Status"
              >
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{option.indicator}</span>
                      <span>{option.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowCreateDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateEvent}
            startIcon={<SaveIcon />}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
            }}
          >
            Create Event
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimelineFilters; 
